import { useState, useEffect, useCallback } from 'react';
import {
  getVendas, getVenda,
  getVendasHorario, getDashboardTopDepartamentos,
  getVendasPorPagamento, getVendasTopProdutos,
} from '../services/api';
import { PageHeader, SearchBar, Table, Loading, Erro, Badge, Paginacao } from '../components/UI';
import FiltroData from '../components/FiltroData';
import './Vendas.css';

const LIMITE = 50;
const fmt  = v  => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtN = v  => (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtI = v  => (v ?? 0).toLocaleString('pt-BR');

const ABAS = [
  { id: 'detalhada',    label: 'Detalhada' },
  { id: 'horario',      label: 'Por Horário' },
  { id: 'departamento', label: 'Por Departamento' },
  { id: 'pagamento',    label: 'Por Pagamento' },
  { id: 'top-produtos', label: 'Top Produtos' },
];

// ─── Bar Chart ────────────────────────────────────────────────────────────────

function BarChart({ dados, labelKey, valueKey, qtyKey, fmtValor, labelWide = false }) {
  if (!dados?.length) return <div className="chart-vazio">Sem dados para o período.</div>;
  const max = Math.max(...dados.map(d => parseFloat(d[valueKey]) || 0), 0.01);
  return (
    <div className="chart-bars">
      {dados.map((d, i) => {
        const pct = (parseFloat(d[valueKey]) / max) * 100;
        return (
          <div key={i} className="chart-row">
            <span className={`chart-label${labelWide ? ' chart-label--wide' : ''}`}>{d[labelKey]}</span>
            <div className="chart-track">
              <div className="chart-bar" style={{ width: `${Math.max(pct, 1)}%` }} />
            </div>
            <div className="chart-meta">
              <span className="chart-value">{fmtValor ? fmtValor(d[valueKey]) : d[valueKey]}</span>
              {qtyKey && <span className="chart-qty">{fmtI(d[qtyKey])} vend.</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Aba: Detalhada ──────────────────────────────────────────────────────────

const colunasVenda = [
  { key: 'ID',         label: '#',       width: 70,  render: v => <span style={{fontWeight:700,color:'var(--laranja)'}}>{v}</span> },
  { key: 'EMISSAO',    label: 'Data',    width: 100, render: v => v?.split('T')[0] || '—' },
  { key: 'CLIENM',     label: 'Cliente', width: 220, render: v => <strong>{v || '—'}</strong> },
  { key: 'VENDEDORNM', label: 'Vendedor',width: 160 },
  { key: 'PDV',        label: 'PDV',     width: 70 },
  { key: 'CUPOM',      label: 'Cupom',   width: 80 },
  { key: 'SUBTOTAL',   label: 'Subtotal',width: 110, render: v => <span style={{fontSize:12,color:'var(--text-secondary)'}}>{fmt(v)}</span> },
  { key: 'TOTAL',      label: 'Total',   width: 110, render: v => <span style={{fontWeight:700,color:'var(--laranja)'}}>{fmt(v)}</span> },
  { key: 'STATUS',     label: 'Status',  width: 100, render: v => {
    const label = v === 1 ? 'Confirmada' : v === 2 ? 'Cancelada' : v === 3 ? 'Estornada' : '—';
    const tipo  = v === 1 ? 'success'    : v === 2 ? 'danger'    : v === 3 ? 'warning'   : 'default';
    return <Badge valor={label} tipo={tipo} />;
  }},
];

const colunasItens = [
  { key: 'PRODUTOID',     label: 'Cód',     width: 80 },
  { key: 'PRODUTONOME',   label: 'Produto', width: 260 },
  { key: 'QUANTIDADE',    label: 'Qtd',     width: 70, render: v => <span style={{fontWeight:600}}>{v}</span> },
  { key: 'VENDA_UNIDADE', label: 'Un',      width: 50 },
  { key: 'VENDA_UNITARIA',label: 'Unit.',   width: 100, render: v => <span style={{fontSize:12}}>{fmt(v)}</span> },
  { key: 'SUBTOTAL',      label: 'Subtotal',width: 110, render: v => <span style={{fontSize:12}}>{fmt(v)}</span> },
  { key: 'TOTAL',         label: 'Total',   width: 110, render: v => <span style={{fontWeight:700,color:'var(--laranja)'}}>{fmt(v)}</span> },
];

const colunasPagamentos = [
  { key: 'FINALIZADORANM', label: 'Forma de Pagamento', width: 200, render: v => <strong>{v || '—'}</strong> },
  { key: 'VALOR',          label: 'Valor',              width: 120, render: v => <span style={{fontWeight:700,color:'var(--laranja)'}}>{fmt(v)}</span> },
  { key: 'TEF_BANDEIRA',   label: 'Bandeira',           width: 110, render: v => v ? <span>{v}</span> : <span style={{color:'var(--text-dim)'}}>—</span> },
  { key: 'TEF_REDE',       label: 'Rede',               width: 110, render: v => v ? <span>{v}</span> : <span style={{color:'var(--text-dim)'}}>—</span> },
  { key: 'TEF_NSU',        label: 'NSU',                width: 130, render: v => v ? <span style={{fontSize:12}}>{v}</span> : <span style={{color:'var(--text-dim)'}}>—</span> },
];

function Info({ label, valor, destaque }) {
  return (
    <div className="vnd-info">
      <span className="vnd-info-label">{label}</span>
      <span className={`vnd-info-valor${destaque ? ' vnd-info--destaque' : ''}`}>{valor || '—'}</span>
    </div>
  );
}

function TabDetalhada({ periodo }) {
  const [busca,  setBusca]  = useState('');
  const [dados,  setDados]  = useState([]);
  const [load,   setLoad]   = useState(false);
  const [erro,   setErro]   = useState('');
  const [offset, setOffset] = useState(0);
  const [modal,  setModal]  = useState(null);
  const [loadM,  setLoadM]  = useState(false);

  const carregar = useCallback(async (b = busca, o = offset) => {
    setLoad(true); setErro('');
    try { setDados(await getVendas({ busca: b, di: periodo.di, df: periodo.df, limite: LIMITE, offset: o })); }
    catch (e) { setErro(e.message); }
    finally { setLoad(false); }
  }, [busca, offset, periodo]);

  useEffect(() => { setOffset(0); carregar(busca, 0); }, [periodo]); // eslint-disable-line
  useEffect(() => { carregar(); }, [offset]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); carregar(busca, 0); }, 400);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line

  const abrirVenda = async (row) => {
    setLoadM(true); setModal(null);
    try { setModal(await getVenda(row.ID)); }
    catch (e) { alert(e.message); }
    finally { setLoadM(false); }
  };

  return (
    <>
      <div className="vnd-busca-bar">
        <SearchBar value={busca} onChange={setBusca} placeholder="Cliente ou documento..." />
      </div>
      {erro && <Erro mensagem={erro} onRetry={carregar} />}
      {load ? <Loading /> : <>
        <Table colunas={colunasVenda} dados={dados} onRowClick={abrirVenda} vazio="Nenhuma venda encontrada." />
        <Paginacao offset={offset} limite={LIMITE} total={dados.length} onChange={setOffset} />
      </>}

      {(modal || loadM) && (
        <div className="vnd-overlay" onClick={() => setModal(null)}>
          <div className="vnd-modal fade-in" onClick={e => e.stopPropagation()}>
            <div className="vnd-modal-header">
              <div>
                <span className="vnd-modal-titulo">Venda #{modal?.ID}</span>
                {modal && <Badge valor={modal.STATUS === 1 ? 'Confirmada' : modal.STATUS === 2 ? 'Cancelada' : modal.STATUS === 3 ? 'Estornada' : '—'} tipo={modal.STATUS === 1 ? 'success' : modal.STATUS === 2 ? 'danger' : modal.STATUS === 3 ? 'warning' : 'default'} />}
              </div>
              <button className="vnd-modal-fechar" onClick={() => setModal(null)}>✕</button>
            </div>
            {loadM ? <Loading /> : modal && (
              <div className="vnd-modal-corpo">
                <div className="vnd-info-grid">
                  <Info label="Data"     valor={modal.EMISSAO?.split('T')[0]} />
                  <Info label="Cliente"  valor={modal.CLIENM} />
                  <Info label="Doc"      valor={modal.CLIEDOC} />
                  <Info label="Vendedor" valor={modal.VENDEDORNM} />
                  <Info label="PDV"      valor={modal.PDV} />
                  <Info label="Cupom"    valor={modal.CUPOM} />
                  <Info label="Subtotal" valor={fmt(modal.SUBTOTAL)} />
                  <Info label="Total"    valor={fmt(modal.TOTAL)} destaque />
                </div>
                <div className="vnd-itens-titulo">Itens da Venda</div>
                <Table colunas={colunasItens} dados={modal.itens || []} vazio="Sem itens." />
                <div className="vnd-itens-titulo">Pagamentos</div>
                <Table colunas={colunasPagamentos} dados={modal.pagamentos || []} vazio="Sem pagamentos." />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Aba: Por Horário ─────────────────────────────────────────────────────────

function TabHorario({ periodo }) {
  const [dados, setDados] = useState([]);
  const [load,  setLoad]  = useState(false);
  const [erro,  setErro]  = useState('');

  useEffect(() => {
    setLoad(true); setErro('');
    getVendasHorario(periodo.di, periodo.df)
      .then(rows => setDados(rows.map(r => ({ ...r, HORA_LABEL: `${String(r.HORA).padStart(2,'0')}h` }))))
      .catch(e => setErro(e.message))
      .finally(() => setLoad(false));
  }, [periodo.di, periodo.df]);

  return (
    <div className="vnd-card">
      <div className="vnd-card-titulo">Vendas por Horário</div>
      {erro && <Erro mensagem={erro} onRetry={() => {}} />}
      {load ? <Loading /> : (
        <>
          <BarChart dados={dados} labelKey="HORA_LABEL" valueKey="VALOR" qtyKey="QTD" fmtValor={fmt} />
          {dados.length > 0 && (
            <div className="vnd-tbl-wrap">
              <table className="vnd-tbl">
                <thead><tr><th>Hora</th><th className="vnd-right">Vendas</th><th className="vnd-right">Faturamento</th></tr></thead>
                <tbody>
                  {dados.map((d, i) => (
                    <tr key={i}>
                      <td>{d.HORA_LABEL}</td>
                      <td className="vnd-right">{fmtI(d.QTD)}</td>
                      <td className="vnd-right vnd-bold">{fmt(d.VALOR)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Aba: Por Departamento ────────────────────────────────────────────────────

function TabDepartamento({ periodo }) {
  const [dados, setDados] = useState([]);
  const [load,  setLoad]  = useState(false);
  const [erro,  setErro]  = useState('');

  useEffect(() => {
    setLoad(true); setErro('');
    getDashboardTopDepartamentos(periodo.di, periodo.df, 30)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setLoad(false));
  }, [periodo.di, periodo.df]);

  return (
    <div className="vnd-card">
      <div className="vnd-card-titulo">Vendas por Departamento</div>
      {erro && <Erro mensagem={erro} onRetry={() => {}} />}
      {load ? <Loading /> : (
        <>
          <BarChart dados={dados} labelKey="AGRUPAMENTO" valueKey="TVENDA" qtyKey="QTD" fmtValor={fmt} labelWide />
          {dados.length > 0 && (
            <div className="vnd-tbl-wrap">
              <table className="vnd-tbl">
                <thead>
                  <tr>
                    <th>#</th><th>Departamento</th>
                    <th className="vnd-right">Qtd</th><th className="vnd-right">Custo</th>
                    <th className="vnd-right">Venda</th><th className="vnd-right">Lucro</th>
                    <th className="vnd-right">Margem</th><th className="vnd-right">Markup</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.map((d, i) => (
                    <tr key={i}>
                      <td className="vnd-rank">{i + 1}</td>
                      <td><strong>{d.AGRUPAMENTO || '—'}</strong></td>
                      <td className="vnd-right">{fmtN(d.QTD)}</td>
                      <td className="vnd-right vnd-dim">{fmt(d.TCUSTO)}</td>
                      <td className="vnd-right vnd-bold">{fmt(d.TVENDA)}</td>
                      <td className="vnd-right">{fmt(d.LUCRO_BRUTO)}</td>
                      <td className="vnd-right"><Pct v={d.MARGEM} /></td>
                      <td className="vnd-right"><Pct v={d.MARKUP} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Aba: Por Pagamento ───────────────────────────────────────────────────────

function TabPagamento({ periodo }) {
  const [dados, setDados] = useState([]);
  const [load,  setLoad]  = useState(false);
  const [erro,  setErro]  = useState('');

  useEffect(() => {
    setLoad(true); setErro('');
    getVendasPorPagamento(periodo.di, periodo.df)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setLoad(false));
  }, [periodo.di, periodo.df]);

  return (
    <div className="vnd-card">
      <div className="vnd-card-titulo">Vendas por Forma de Pagamento</div>
      {erro && <Erro mensagem={erro} onRetry={() => {}} />}
      {load ? <Loading /> : (
        <>
          <BarChart dados={dados} labelKey="FINALIZADORA" valueKey="VALOR" qtyKey="QTD" fmtValor={fmt} labelWide />
          {dados.length > 0 && (
            <div className="vnd-tbl-wrap">
              <table className="vnd-tbl">
                <thead><tr><th>Forma de Pagamento</th><th className="vnd-right">Vendas</th><th className="vnd-right">Total</th></tr></thead>
                <tbody>
                  {dados.map((d, i) => (
                    <tr key={i}>
                      <td><strong>{d.FINALIZADORA || '—'}</strong></td>
                      <td className="vnd-right">{fmtI(d.QTD)}</td>
                      <td className="vnd-right vnd-bold">{fmt(d.VALOR)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Aba: Top Produtos ────────────────────────────────────────────────────────

function TabTopProdutos({ periodo }) {
  const [dados, setDados] = useState([]);
  const [load,  setLoad]  = useState(false);
  const [erro,  setErro]  = useState('');

  useEffect(() => {
    setLoad(true); setErro('');
    getVendasTopProdutos(periodo.di, periodo.df, 50)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setLoad(false));
  }, [periodo.di, periodo.df]);

  return (
    <div className="vnd-card">
      <div className="vnd-card-titulo">Top Produtos Mais Vendidos</div>
      {erro && <Erro mensagem={erro} onRetry={() => {}} />}
      {load ? <Loading /> : (
        <div className="vnd-tbl-wrap">
          <table className="vnd-tbl">
            <thead>
              <tr>
                <th>#</th><th>Produto</th><th>Código</th>
                <th className="vnd-right">Qtd</th>
                <th className="vnd-right">Venda</th>
                <th className="vnd-right">Custo</th>
                <th className="vnd-right">Lucro</th>
                <th className="vnd-right">Margem</th>
                <th className="vnd-right">Markup</th>
                <th className="vnd-right">Preço Unit.</th>
              </tr>
            </thead>
            <tbody>
              {dados.length === 0 && (
                <tr><td colSpan={10} style={{textAlign:'center',padding:'32px',color:'var(--text-dim)'}}>Sem dados para o período.</td></tr>
              )}
              {dados.map((d, i) => (
                <tr key={i}>
                  <td className="vnd-rank">{i + 1}</td>
                  <td><strong>{d.PRODUTO || '—'}</strong></td>
                  <td style={{color:'var(--text-dim)',fontSize:12}}>{d.CODIGO || '—'}</td>
                  <td className="vnd-right">{fmtN(d.QTD)}</td>
                  <td className="vnd-right vnd-bold">{fmt(d.TVENDA)}</td>
                  <td className="vnd-right vnd-dim">{fmt(d.TCUSTO)}</td>
                  <td className="vnd-right">{fmt(d.LUCRO_BRUTO)}</td>
                  <td className="vnd-right"><Pct v={d.MARGEM} /></td>
                  <td className="vnd-right"><Pct v={d.MARKUP} /></td>
                  <td className="vnd-right">{fmt(d.PRECO_UNIT)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function Pct({ v }) {
  if (v == null) return <span style={{color:'var(--text-dim)'}}>—</span>;
  return (
    <span className={`dash-pct ${v >= 0 ? 'dash-pct--pos' : 'dash-pct--neg'}`}>
      {fmtN(v)}%
    </span>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Vendas() {
  const [periodo, setPeriodo] = useState(null);
  const [aba,     setAba]     = useState('detalhada');

  return (
    <div>
      <PageHeader titulo="Vendas" subtitulo={periodo?.label || ''}>
        <FiltroData onChange={setPeriodo} defaultModo="mes" />
      </PageHeader>

      <div className="tabs-bar">
        {ABAS.map(a => (
          <button
            key={a.id}
            className={`tab-btn${aba === a.id ? ' tab-btn--ativo' : ''}`}
            onClick={() => setAba(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {periodo?.di && (
        <>
          {aba === 'detalhada'    && <TabDetalhada    periodo={periodo} />}
          {aba === 'horario'      && <TabHorario      periodo={periodo} />}
          {aba === 'departamento' && <TabDepartamento periodo={periodo} />}
          {aba === 'pagamento'    && <TabPagamento    periodo={periodo} />}
          {aba === 'top-produtos' && <TabTopProdutos  periodo={periodo} />}
        </>
      )}
    </div>
  );
}
